import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type OutletResource,
  OutletsController_list_v1,
  type StockLevelResource,
  StockLevelsController_read_v1,
  type StocktakeCountResource,
  type StocktakeResource,
  StocktakesController_record_v1,
  type StocktakesController_record_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconCircleCheck,
  IconWifiOff,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type Control, useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isOffline, isWorthRetrying, messageForFailure } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { IDEMPOTENCY_KEY_HEADER } from '@/lib/idempotency-key';
import { formatInstant } from '@/lib/instant';
import { isoInstantFrom, localInstantOf } from '@/lib/local-instant';
import { attemptFor, type WriteAttempt } from '@/lib/write-attempt';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { Screen, ScreenFact, ScreenNote, ScreenTable } from '../back-office/Screen';
import { isRetired } from '../outlets/outlet-kinds';
import { paths } from '../paths';
import { formatLevel } from './stock-levels';
import {
  blankCount,
  countsEntered,
  fingerprintOf,
  stocktakeSchema,
  type StocktakeValues,
} from './stocktake-form';
import { countsAgreed, countsSettled, formatVariance, outcomeSays } from './stocktake-variance';

/**
 * Somebody walked the shelf and counted it:
 * `POST /v1/outlets/{outletId}/stock/stocktakes`.
 *
 * ## The person enters what they counted, and the screen shows them nothing to
 * copy
 *
 * This is the whole ticket and every other decision here follows from it. There
 * is no difference field, no `+/-` adjustment and no arithmetic anybody is asked
 * to do — the server computes the variance and writes the compensating movement
 * itself (CL-503, FR-539b). A form that asked for a delta would reintroduce the
 * mistyped sign that the route was designed to make unreachable.
 *
 * **The figure the system believes is not on this screen before the count, and it
 * is not merely hidden — it never arrives.** The item list comes from `GET
 * /v1/outlets/{outletId}/stock`, which answers a `level` per item, and
 * {@link countableItem} drops it the moment the response lands. Everything below
 * therefore holds `{ id, code, name }` and *cannot* render a level by accident,
 * because there is none in the value. That is deliberate: showing somebody the
 * expected figure beside an empty box invites them to type it back, and a
 * stocktake that agrees because the number was on screen is worse than no
 * stocktake at all — it is a false confirmation written into an append-only log.
 *
 * The counted-against-computed comparison appears **after** the `201`, from the
 * server's own answer, where it is a result rather than a prompt.
 *
 * ## An empty box and a zero are different answers
 *
 * A row is offered for every item the outlet stocks, all of them blank. A box
 * left empty means nobody counted that item and it is left out of the submission;
 * `0` means the shelf was found empty, which the route accepts (its floor is
 * zero, unlike a delivery's or a correction's) and which is the count that finds
 * a whole shelf gone. `stocktake-form.ts` holds that rule; nothing here restates
 * it as a second opinion.
 *
 * ## A count that agreed is a completed count
 *
 * `variance: 0` and `movementId: null` is the answer "we counted, and the books
 * were right" (FR-539c). It is shown as a recorded line with a badge saying so,
 * never as a no-op and never dropped from the table — the record that the shelf
 * was walked is the point of walking it. `stocktake-variance.ts` owns the
 * wording, and the same module says why a negative `computedQuantity` is shown as
 * it stands (CL-501).
 *
 * ## What the outlet picker offers
 *
 * Outlets that carry no stock are not offered, for the reason
 * `RecordCorrection.tsx` gives in full: `STOCK_OUTLET_CARRIES_NO_STOCK` is a fact
 * about the place rather than about anything on the form. The refusal stays
 * mapped, for an outlet changed while the screen was open.
 */

/** What this screen is allowed to know about an item. */
interface CountableItem {
  readonly catalogueItemId: string;
  readonly code: string;
  readonly name: string;
}

/**
 * An item to count, with the figure the system believes deliberately dropped.
 *
 * **The one line that keeps this screen honest.** `StockLevelResource` carries
 * `level`, and every other stock screen renders it; here it is discarded at the
 * boundary so that no cell, no rail fact and no future edit can put the expected
 * number in front of somebody holding a clipboard. If a change ever needs the
 * level before the count, it needs a different design and not a wider type.
 */
const countableItem = (item: StockLevelResource): CountableItem => ({
  catalogueItemId: item.catalogueItemId,
  code: item.code,
  name: item.name,
});

/** One empty array, so "no items yet" is the same value on every render. */
const NO_ITEMS: readonly CountableItem[] = [];

/** Every state the outlet's items can be in. One value, so two cannot coexist. */
type ItemsState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly items: readonly CountableItem[] }
  | { readonly kind: 'failed'; readonly error: unknown };

export function RecordStocktake() {
  const client = useApiClient();

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);

  // Whether the outlets have answered *at all*, which is not the same question
  // as whether there are any. Without it the notice under an empty picker reads
  // as a statement about the park for as long as the first request takes.
  const [outletsAnswered, setOutletsAnswered] = useState(false);

  const [items, setItems] = useState<ItemsState>({ kind: 'idle' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const [formError, setFormError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<StocktakeResource | null>(null);

  /**
   * The key and the id this form is currently attempting under.
   *
   * A ref rather than state: nothing renders from it, and it must be read and
   * written inside one submit without waiting for a render to see the new value.
   */
  const attempt = useRef<WriteAttempt | null>(null);

  const form = useForm<StocktakeValues>({
    resolver: zodResolver(stocktakeSchema),
    mode: 'onTouched',
    defaultValues: {
      outletId: '',
      // Prefilled with now, from the injected clock rather than from `new Date()`
      // — a shelf is usually entered as it is walked. It stays editable because a
      // count taken this morning and typed up this evening has to be able to say
      // when it was taken.
      at: localInstantOf(systemClock),
      // Nothing until an outlet is chosen. The rows are the outlet's items and
      // there is no such thing as a park-wide shelf.
      counts: [],
    },
  });

  const outletId = useWatch({ control: form.control, name: 'outletId' });
  const rows = useWatch({ control: form.control, name: 'counts' });

  const pending = form.formState.isSubmitting;

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: the outlets are bounded by the park, which is the case
    // `docs/ui-conventions.md` gives `collectPages` for. Retired outlets are
    // excluded by the API's own default — a retired outlet has no shelf to walk.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (!live) return;

        setOutlets(all);
        setOutletsAnswered(true);
      })
      .catch(() => {
        // The picker is the only casualty and the screen says so where the
        // picker is, rather than replacing a form somebody may be halfway
        // through with an error about a control.
        //
        // Not `outletsAnswered`: a failed read has answered nothing about which
        // outlets carry stock, so the notice keeps the wording that is true
        // either way.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    if (outletId === '') {
      setItems({ kind: 'idle' });

      return;
    }

    // Two guards, not one. `controller` stops the request the moment the outlet
    // changes, and `live` stops an answer already in flight from landing after
    // this effect was torn down — without it, switching outlets twice quickly
    // can leave the second outlet's items underneath the first's.
    const controller = new AbortController();
    let live = true;

    setItems({ kind: 'loading' });

    client
      .request(StockLevelsController_read_v1, {
        path: { outletId },
        // No `asOf`. What is being asked for is the list of items this outlet
        // stocks *now*, which is the set of shelves somebody is standing in
        // front of. The levels in the answer are dropped on the next line.
        query: {},
        signal: controller.signal,
      })
      .then((stock) => {
        if (!live) return;

        const countable = stock.items.map(countableItem);

        setItems({ kind: 'loaded', items: countable });

        // One blank row per item, in the order the API sent them — by code,
        // which is the order a printed shelf list is in. `setValue` rather than
        // a field array: nothing here adds or removes a row, so there is no
        // control for a field array to serve, and `form` is a stable reference
        // that this effect can depend on honestly.
        form.setValue(
          'counts',
          countable.map((item) => blankCount(item.catalogueItemId)),
        );
      })
      .catch((error: unknown) => {
        if (live) setItems({ kind: 'failed', error });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, outletId, reloads, form]);

  // Only outlets that hold goods. An outlet that carries no stock is refused
  // `STOCK_OUTLET_CARRIES_NO_STOCK` by every one of the three write routes, so
  // offering one would be offering a form that cannot be submitted.
  const stockedOutlets = useMemo(() => outlets.filter((outlet) => outlet.carriesStock), [outlets]);

  // Base UI resolves what the trigger shows from `items` and not from the
  // rendered options, so a picker without it shows a bare UUID where an outlet
  // name belongs. Derived from the same rows the options render.
  const outletItems = useMemo(
    () => stockedOutlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    [stockedOutlets],
  );

  const outlet = stockedOutlets.find((candidate) => candidate.id === outletId);

  const shelves: readonly CountableItem[] = items.kind === 'loaded' ? items.items : NO_ITEMS;

  // Nothing to count: the outlet stocks no item that counts stock. Said where the
  // rows are rather than as a refusal after the form is filled in.
  const nothingToCount = items.kind === 'loaded' && shelves.length === 0;

  // What has been answered for so far, from the same predicate the submission
  // uses — so the number somebody watches climb is the number that will be sent.
  const entered = countsEntered(rows).length;

  const countErrors = form.formState.errors.counts;
  const countsMessage = countErrors?.root?.message ?? countErrors?.message;

  /**
   * A refusal, put where the thing it is about is.
   *
   * The copy is never written here. `messageForFailure` turns the code into a
   * sentence and is the only thing that does; what this decides is *which field*
   * the sentence belongs to.
   *
   * `STOCK_STOCKTAKE_IMMUTABLE` is deliberately not among the cases: it is about
   * the count as a whole rather than any field, its copy already says a recount
   * is the way forward, and it falls through to the message above the button.
   */
  const recordFailure = (error: unknown) => {
    if (error instanceof ApiError && error.code === 'STOCK_OUTLET_CARRIES_NO_STOCK') {
      // A fact about the place. The picker does not offer such an outlet, so
      // this is one changed while the screen was open.
      form.setError('outletId', { message: messageForFailure(error) });

      return;
    }

    if (error instanceof ApiError && error.code === 'STOCK_ITEM_NOT_TRACKED') {
      // The refusal carries the ids it is about in `details`, and the error map
      // deliberately does not name them — a screen holding the rows knows which
      // ones it sent and says so itself. Anything it cannot match falls through
      // to the form-level message below, so a `details` shape that changes loses
      // the marker rather than the message.
      const named = namedItemIds(error);
      const marked = form.getValues('counts').reduce((count, row, index) => {
        if (!named.has(row.catalogueItemId)) return count;

        form.setError(`counts.${index}.countedQuantity`, {
          message: messageForFailure(error),
        });

        return count + 1;
      }, 0);

      if (marked > 0) return;
    }

    setFormError(messageForFailure(error));
  };

  const handleSubmit = async (values: StocktakeValues) => {
    // Belt and braces with the disabled button below. A recorded count is
    // immutable and a second one is a second walk of the shelf, so a submit that
    // got past both would be a count nobody took.
    if (pending) return;

    // The resolver has already refused everything this can be null for. Read as
    // an impossible state rather than asserted away with a `!`: a change to the
    // schema that let a bad value through shows as a field that refuses rather
    // than as a body carrying `undefined`.
    const occurredAt = isoInstantFrom(values.at);

    if (occurredAt === null) {
      form.setError('at', { message: 'That is not a real date and time.' });

      return;
    }

    setFormError(null);
    setRecorded(null);

    // Everything but the id, which is minted with the key below because the two
    // together are what makes one attempt one write.
    const content = {
      occurredAt,

      // Only the rows somebody answered for. An untouched box is an item nobody
      // counted and must not become a count of zero — that would write a
      // movement taking the outlet's whole holding away, silently and
      // irreversibly.
      counts: countsEntered(values.counts).map((row) => ({
        catalogueItemId: row.catalogueItemId,

        // The figure as counted. The schema has already refused anything that is
        // not digits, so this cannot be negative and cannot be fractional — and
        // nothing here subtracts anything from anything, because the variance is
        // the server's to derive.
        countedQuantity: Number(row.countedQuantity),
      })),
    };

    const sending = attemptFor(attempt.current, fingerprintOf(values.outletId, content));

    attempt.current = sending;

    try {
      // The body's shape comes from the generated client. Nothing about this
      // route's path or payload is written down in this file.
      const body: StocktakesController_record_v1Body = { id: sending.id, ...content };

      const answer = await client.request(StocktakesController_record_v1, {
        path: { outletId: values.outletId },
        body,
        headers: { [IDEMPOTENCY_KEY_HEADER]: sending.key },
      });

      setRecorded(answer);

      // The count is written, so the next one is a different act however similar
      // it looks — counting the same shelf twice in a day is two real counts and
      // must not replay the first one's answer.
      attempt.current = null;

      // The boxes are emptied and the rows stay, because the common case after
      // one count is another shelf at the same outlet. The moment is refreshed
      // rather than left at the old one, which would quietly backdate the next
      // count.
      form.reset({
        outletId: values.outletId,
        at: localInstantOf(systemClock),
        counts: values.counts.map((row) => blankCount(row.catalogueItemId)),
      });
    } catch (error: unknown) {
      // Nothing is cleared. Re-entering a whole shelf to fix one box is a cruelty
      // this form is in no position to inflict. The attempt is deliberately left
      // in place too — if the body is unchanged, pressing again replays under the
      // same key rather than counting twice.
      recordFailure(error);
    }
  };

  return (
    <Screen
      title="Record a stocktake"
      description="Walk the shelf, then enter what you counted — the figure itself, never a difference. The system works out how far off its own records were and settles them; you will see both side by side once it is recorded."
      // Only when there is something standing to say. Both facts are
      // conditional and the navigation links that used to hold the bar up have
      // moved to the margin; `Screen` draws the band for a fragment that
      // renders nothing, which on an untouched form is an empty ruled strip.
      aside={
        outlet === undefined && !(items.kind === 'loaded' && shelves.length > 0) ? undefined : (
          <>
            {outlet !== undefined && (
              <ScreenFact label="Outlet">
                <span className="font-mono numeric">{outlet.code}</span> — {outlet.name}
                {isRetired(outlet) && (
                  <>
                    {' '}
                    <Badge variant="outline">Retired</Badge>
                  </>
                )}
              </ScreenFact>
            )}

            {items.kind === 'loaded' && shelves.length > 0 && (
              <ScreenFact label="Counted so far">
                <span className="font-mono numeric">{String(entered)}</span> of{' '}
                <span className="font-mono numeric">{String(shelves.length)}</span>{' '}
                {shelves.length === 1 ? 'item' : 'items'}
                <br />
                <span className="text-muted-foreground">
                  A box left empty is an item nobody counted. Count as many or as few as you walked.
                </span>
              </ScreenFact>
            )}
          </>
        )
      }
      // What a count *is*, beside the boxes rather than under them. The
      // sentence about the two clocks explains rather than instructs, so it
      // reads once here instead of under a field somebody is tabbing past.
      guidance={
        <>
          <ScreenNote label="Two clocks">
            The time you enter is recorded as the human fact; the server keeps its own clock for
            ordering and for which day this belongs to.
          </ScreenNote>

          {/* The related screens, at the foot of the margin behind a rule.
              They were in the `aside` bar, which is filters and counts. */}
          <div className="space-y-2 border-t border-border pt-4 text-sm">
            <Link
              to={paths.stock}
              className="inline-flex items-center gap-1.5 rounded-3xl text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
            >
              What this outlet holds
              <IconArrowRight className="size-4" aria-hidden />
            </Link>
            <br />
            <Link
              to={paths.stockMovements}
              className="inline-flex items-center gap-1.5 rounded-3xl text-muted-foreground underline-offset-4 outline-none hover:text-foreground hover:underline focus-visible:ring-3 focus-visible:ring-ring/30"
            >
              Every movement, newest first
              <IconArrowRight className="size-4" aria-hidden />
            </Link>
          </div>
        </>
      }
    >
      {recorded !== null && <Counted stocktake={recorded} items={shelves} />}

      <Form {...form}>
        {/* One column, per docs/ui-conventions.md. `noValidate` because the
              browser's own bubbles are not this app's error language. */}
        <form
          className="space-y-6"
          noValidate
          onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
        >
          <FormField
            control={form.control}
            name="outletId"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  Outlet
                </FormLabel>
                {/* Not wrapped in `FormControl`: the trigger is a button
                      rather than the input the generated id belongs on. */}
                <Select
                  value={field.value}
                  items={outletItems}
                  onValueChange={(next) => {
                    // Base UI types the value as nullable because a select may
                    // be cleared. `''` is what this form calls "nothing
                    // chosen", and the schema refuses it.
                    field.onChange(next ?? '');

                    // The rows counted items at the *previous* outlet. Left
                    // standing they would be sent against a place that may not
                    // stock them at all. Cleared here and refilled when the new
                    // outlet's items land.
                    form.setValue('counts', []);
                    setRecorded(null);
                    setFormError(null);
                  }}
                  disabled={stockedOutlets.length === 0}
                >
                  <SelectTrigger
                    id="stocktake-outlet"
                    aria-label="Outlet"
                    aria-invalid={form.formState.errors.outletId !== undefined}
                    aria-describedby={
                      form.formState.errors.outletId === undefined
                        ? undefined
                        : 'stocktake-outlet-error'
                    }
                    className={`${fieldSurface} h-11 w-full`}
                  >
                    <SelectValue placeholder="Choose an outlet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {stockedOutlets.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormDescription>
                  {outletsAnswered && stockedOutlets.length === 0
                    ? 'No outlet in reach carries stock, so there is no shelf here to count. An owner sets an outlet up to hold goods.'
                    : 'Only outlets that hold goods are listed. A shelf is counted one outlet at a time.'}
                </FormDescription>
                <FormMessage id="stocktake-outlet-error" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="at"
            render={({ field }) => (
              <FormItem>
                <FormLabel required className={fieldLabel}>
                  When it was counted
                </FormLabel>
                <FormControl
                  render={
                    <Input
                      // A native date-and-time field rather than a box asking
                      // for ISO 8601, and typed in the zone every instant in
                      // this app is rendered in.
                      type="datetime-local"
                      className={`${fieldSurface} h-11 w-full numeric`}
                      required
                      {...field}
                    />
                  }
                />
                <FormDescription>
                  Filled in with now. Change it for a shelf walked earlier.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <fieldset className="space-y-4 border-t border-border pt-6">
            <legend className="sr-only">What you counted</legend>

            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-medium text-foreground">What you counted</p>
              <p className="text-sm text-muted-foreground">
                The figure on the shelf, not a difference.
              </p>
            </div>

            {items.kind === 'idle' && (
              <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
                Choose an outlet first. The items it stocks are the shelves there are to count.
              </p>
            )}

            {items.kind === 'loading' && (
              <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
                Reading what this outlet stocks…
              </p>
            )}

            {items.kind === 'failed' && (
              <ItemsFailed error={items.error} onRetry={() => setReloads((count) => count + 1)} />
            )}

            {nothingToCount && (
              <p className="rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
                This outlet stocks no item that counts stock, so there is no shelf here to count. An
                owner makes an item available at an outlet, under where it sells.
              </p>
            )}

            {items.kind === 'loaded' && shelves.length > 0 && (
              <>
                <p className="text-sm text-muted-foreground">
                  Every item this outlet stocks, in code order. Enter what you counted;{' '}
                  <span className="text-foreground">
                    leave a box empty for an item you did not count
                  </span>
                  , and enter <span className="font-mono text-foreground numeric">0</span> for a
                  shelf you found empty.
                </p>

                {/* One list with hairline dividers rather than a card each: a
                      shelf list is read straight down, and a bordered block per
                      item turns twenty items into twenty things to look at. */}
                <div className="divide-y divide-border rounded-2xl border border-border">
                  {shelves.map((item, index) => (
                    <CountRow
                      key={item.catalogueItemId}
                      item={item}
                      index={index}
                      control={form.control}
                    />
                  ))}
                </div>
              </>
            )}

            {/* The array's own message — "you have not counted anything", "too
                  many items" — has no field to sit beside, so it sits under the
                  set it is about. Read from `root` first and from the array node
                  second, because react-hook-form puts an array-level error in
                  either place depending on who set it. */}
            {countsMessage !== undefined && (
              <p className="text-sm text-destructive">{countsMessage}</p>
            )}
          </fieldset>

          {/* Above the submit and it stays there — a toast vanishes, and
                somebody who looked away has no way to get it back. */}
          {formError !== null && (
            <Alert variant="destructive">
              <IconAlertTriangle />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            // The one primary action on the screen, and the only control that
            // writes anything. Disabled while in flight, which is what stops
            // one walk of the shelf being recorded twice.
            disabled={pending || nothingToCount || items.kind !== 'loaded'}
          >
            {pending ? 'Recording…' : 'Record count'}
          </Button>
        </form>
      </Form>
    </Screen>
  );
}

/**
 * One item, and the box for what was counted on its shelf.
 *
 * **The item's own name is the field's label**, which is both what a screen
 * reader needs and what the eye is already reading. There is no `required`
 * marker: an item nobody counted is a legitimate answer, and the rule that at
 * least one box must be filled belongs to the set rather than to any row.
 *
 * **There is nothing else in the row.** No level, no last count, no expected
 * figure — see the note at the top of this file for why that is a design
 * decision rather than an omission.
 */
function CountRow({
  item,
  index,
  control,
}: {
  item: CountableItem;
  index: number;
  control: Control<StocktakeValues>;
}) {
  return (
    <FormField
      control={control}
      name={`counts.${index}.countedQuantity`}
      render={({ field }) => (
        <FormItem className="flex flex-wrap items-start justify-between gap-4 px-4 py-3">
          <FormLabel className="flex-1 text-sm font-normal text-foreground">
            <span className="font-mono numeric">{item.code}</span>
            <span className="ml-2">{item.name}</span>
          </FormLabel>

          <div className="w-32 space-y-1">
            <FormControl
              render={
                <Input
                  // `inputMode` and not `type="number"`, which hands the browser
                  // its own opinion — a spinner, a wheel that changes the count
                  // under a pointer, and a value that reads back empty as `NaN`.
                  // A count is read back and typed in, so it gets the mono face
                  // and tabular figures.
                  inputMode="numeric"
                  autoComplete="off"
                  // **A deliberate departure from `docs/ui-conventions.md`**,
                  // which asks a placeholder to show the shape of a good answer
                  // — `GATE1`, not "Enter code". A specimen number here would be
                  // a grey figure sitting in every empty box of a thirty-item
                  // list, which is the one thing this screen must never do: it
                  // reads as a suggested count. What the box actually needs to
                  // say while it is empty is what an empty box *means*, because
                  // blank and `0` are different answers and only one of them
                  // writes a movement.
                  placeholder="Not counted"
                  className={`${fieldSurface} h-11 w-full font-mono numeric`}
                  {...field}
                />
              }
            />
            <FormMessage />
          </div>
        </FormItem>
      )}
    />
  );
}

/**
 * The outlet's items could not be read.
 *
 * Not `LoadFailed`, which words itself as a list that could not be loaded and
 * sits inside a table. This is a form that cannot be filled in yet, and offline
 * is said as the operating mode it is rather than as a fault.
 */
function ItemsFailed({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <Alert>
      {isOffline(error) ? <IconWifiOff /> : <IconAlertTriangle />}
      <AlertTitle>
        {isOffline(error)
          ? 'No connection to the server'
          : 'What this outlet stocks is unavailable'}
      </AlertTitle>
      <AlertDescription>
        <span>{messageForFailure(error)}</span>
        {isWorthRetrying(error) && (
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
            Try again
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * What the count found, from the server's answer and from nothing else.
 *
 * This is the only place on the screen where a counted figure and a computed one
 * appear together, and the ordering is the point: the person counted first, and
 * the comparison is a result they are shown rather than a target they were given.
 *
 * Every line is listed, including the ones that agreed. `variance: 0` with
 * `movementId: null` is a recorded count and reads as one (FR-539c).
 */
function Counted({
  stocktake,
  items,
}: {
  stocktake: StocktakeResource;
  items: readonly CountableItem[];
}) {
  const agreed = countsAgreed(stocktake.counts);
  const settled = countsSettled(stocktake.counts);

  // The names for the ids the answer carries. A count for an item that has since
  // left the list still renders — see `CountedRow`.
  const named = new Map(items.map((item) => [item.catalogueItemId, item]));

  return (
    <div className="space-y-4">
      {/* `status` and not the component's default `alert`: a count that was
          recorded is not an interruption. */}
      <Alert role="status">
        <IconCircleCheck />
        <AlertTitle>Counted</AlertTitle>
        <AlertDescription>
          <span>
            <span className="font-mono numeric">{String(stocktake.counts.length)}</span>{' '}
            {stocktake.counts.length === 1 ? 'item' : 'items'} counted as at{' '}
            <span className="font-mono numeric">{formatInstant(stocktake.occurredAt)}</span>.{' '}
            <span className="font-mono numeric">{String(agreed)}</span> agreed with the records;{' '}
            <span className="font-mono numeric">{String(settled)}</span>{' '}
            {settled === 1 ? 'was' : 'were'} settled by a movement. A count that agreed is recorded
            just as firmly as one that did not — it is the record that this shelf was walked. None
            of it can be amended: a recount is a new stocktake, and both stay in the log.
          </span>
        </AlertDescription>
      </Alert>

      <ScreenTable label="What this count found">
        <TableHeader>
          <TableRow>
            <TableHead className="w-40">Code</TableHead>
            <TableHead>Item</TableHead>
            {/* Quantities right-align, per docs/ui-conventions.md — comparing
                down a column is the task. */}
            <TableHead className="w-32 text-right">Counted</TableHead>
            <TableHead className="w-36 text-right">Records said</TableHead>
            <TableHead className="w-48 text-right">Difference</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {stocktake.counts.map((count) => (
            <CountedRow
              key={count.catalogueItemId}
              count={count}
              item={named.get(count.catalogueItemId)}
            />
          ))}
        </TableBody>
      </ScreenTable>
    </div>
  );
}

/**
 * One counted line, as the server answered it.
 *
 * `computedQuantity` is rendered by `formatLevel` and therefore **as it stands,
 * negative included** (CL-501) — a records figure below zero is the state a count
 * exists to resolve, and clamping it here would hide the very thing this screen
 * just found.
 */
function CountedRow({
  count,
  item,
}: {
  count: StocktakeCountResource;
  item: CountableItem | undefined;
}) {
  return (
    <TableRow>
      <TableCell className="font-mono text-foreground numeric">
        {/* An item that has left the outlet's list between the count and its
            answer has no code here. The id is not shown in its place — it is not
            a thing anybody reads — and the row still carries its figures. */}
        {item?.code ?? '—'}
      </TableCell>

      <TableCell className="whitespace-normal">
        {item?.name ?? 'This item is no longer stocked here'}
      </TableCell>

      <TableCell className="text-right font-mono text-foreground numeric">
        {formatLevel(count.countedQuantity)}
      </TableCell>

      <TableCell className="text-right font-mono text-foreground numeric">
        {formatLevel(count.computedQuantity)}
      </TableCell>

      <TableCell className="text-right">
        <span className="inline-flex items-center justify-end gap-2">
          {/* `outline` and never `destructive`. Red in this interface means the
              system refused something (docs/ui-conventions.md, Colour); a
              variance is a true figure that has already been settled. */}
          <Badge variant="outline">{outcomeSays(count)}</Badge>
          <span className="font-mono text-foreground numeric">
            {formatVariance(count.variance)}
          </span>
        </span>
      </TableCell>
    </TableRow>
  );
}

/**
 * The catalogue items a refusal names, if it names any.
 *
 * `details` is the API's own envelope field and is typed as unknown values, so
 * every step is checked: a shape that changes upstream loses the per-row marker
 * and falls back to the message above the button, rather than throwing inside a
 * catch block.
 */
function namedItemIds(error: ApiError): ReadonlySet<string> {
  const named = error.details?.['catalogueItemIds'];

  if (!Array.isArray(named)) return new Set();

  return new Set(named.filter((value): value is string => typeof value === 'string'));
}
